import { useCallback, useEffect, useRef, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AnimatePresence, m } from "framer-motion";
import {
  AlertCircle,
  ArrowDownLeft,
  ArrowRight,
  ArrowUpRight,
  Banknote,
  CalendarRange,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  CreditCard,
  Download,
  Gift,
  Info,
  LayoutDashboard,
  Lock,
  MousePointerClick,
  Send,
  Stethoscope,
  TrendingUp,
  Upload,
  UserCheck,
  Users,
  Wallet as WalletIcon,
} from "lucide-react";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import { useReferralShare } from "@/components/referral/referral-share-context";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  SwipeableTabsContent,
  type SwipeableTabsHandle,
} from "@/components/ui/swipeable-tabs-content";
import { Switch } from "@/components/ui/switch";
import { TabBar, type TabItem } from "@/components/ui/tab-bar";
import { useBrowsingCountry } from "@/lib/hooks/use-browsing-country";
import { isVaultUnlockCancelled, useProtectedAction } from "@/lib/hooks/use-protected-action";
import { dispatchReferralToggled } from "@/lib/hooks/use-referral-program";
import {
  FISCAL_TYPES,
  type FiscalTypeCode,
  getFiscalLocalLabel,
  getWithholdingTax,
} from "@/lib/fiscal-types";
import { sidebarDataQueryOptions } from "@/lib/queries/sidebar-data";
import { showToast } from "@/lib/ui/toast";
import { clearVaultUnlockHint } from "@/lib/vault-unlock";
import { LoadErrorState } from "@/components/ui/load-error-state";
import { formatMoney } from "@/lib/format-money";

// ---------------------------------------------------------------------------
// Types — mirrors apps/api/src/routes/account-wallet.ts response shapes.
// ---------------------------------------------------------------------------

type TabId = "overview" | "referrals" | "commissions" | "wallet" | "payouts";

const VALID_TABS: readonly TabId[] = [
  "overview",
  "referrals",
  "commissions",
  "wallet",
  "payouts",
] as const;

// Tabs hidden when the country's referral program is disabled (legacy
// wallet/page.tsx gates them via useCountry().referralEnabled).
const REFERRAL_ONLY_TABS: readonly TabId[] = ["referrals", "commissions"] as const;

interface WalletBalance {
  available: number;
  /**
   * Proration credit (a downgrade, removed seats, an interval change). Spent
   * on the next renewals automatically, never paid out — so it is not part of
   * `available`, which is what a payout can take.
   */
  subscriptionCredit?: number;
  currency: string;
  referralCode: string | null;
  referralEnabled: boolean;
  totalReferrals: number;
  referralClickCount: number;
  premiumDaysEarned: number;
  hasPremium: boolean;
  premiumExpiresAt: string | null;
  referralTerms: Record<string, unknown> | null;
}

interface OverviewStats {
  totalReferrals: number;
  activeReferrals: number;
  linkClicks: number;
  pendingCommissions: number;
  totalEarned: number;
}

interface ProjectionItem {
  month: string;
  amount: number;
}

interface ActivityItem {
  id: string;
  type: string;
  amount: number;
  currency: string;
  balanceAfter: number;
  description: string;
  originalAmount: number | null;
  originalCurrency: string | null;
  exchangeRateUsed: number | null;
  createdAt: string;
}

interface CountryPricing {
  monthlyPrice: number;
  currencySymbol: string;
  currencyBefore: boolean;
}

interface OverviewResponse {
  balance: WalletBalance;
  stats: OverviewStats;
  projection: ProjectionItem[];
  recentActivity: ActivityItem[];
  countryPricing: CountryPricing | null;
}

interface Referral {
  id: string;
  name: string;
  type: string;
  status: string;
  planTier: string | null;
  monthlyValue: number | null;
  commissionY1Pct: number;
  commissionY2Pct: number;
  commissionY3Pct: number;
  commissionStartDate: string;
  commissionEndDate: string;
  totalCommission: number;
  country: string;
  createdAt: string;
}

interface ReferralsResponse {
  balance: WalletBalance;
  referrals: Referral[];
  pagination: { page: number; limit: number; total: number };
}

interface CommissionItem {
  id: string;
  referralName: string;
  referralType: string | null;
  planTier: string | null;
  subscriptionAmount: number;
  commissionPct: number;
  commissionAmount: number;
  commissionYear: number;
  status: string;
  originalSubscriptionAmount: number | null;
  originalCommissionAmount: number | null;
  originalCurrency: string | null;
  exchangeRateUsed: number | null;
}

interface CommissionGroup {
  month: number;
  year: number;
  activeReferrals: number;
  totalSubscription: number;
  totalCommission: number;
  status: string;
  items: CommissionItem[];
}

interface CommissionsResponse {
  balance: WalletBalance;
  commissions: CommissionGroup[];
  pagination: { page: number; limit: number; total: number };
}

interface Transaction {
  id: string;
  type: string;
  /** "subscription_credit": the row moved the subscription credit, and `balanceAfter` is ITS balance. */
  ledger?: "wallet" | "subscription_credit";
  amount: number;
  currency: string;
  balanceAfter: number;
  description: string;
  referenceType: string | null;
  referenceId: string | null;
  originalAmount: number | null;
  originalCurrency: string | null;
  exchangeRateUsed: number | null;
  createdAt: string;
}

interface TransactionsResponse {
  balance: WalletBalance;
  wallet: { pending: number; totalEarned: number };
  transactions: Transaction[];
  pagination: { page: number; limit: number; total: number };
}

interface PayoutInfoData {
  /** The IBAN on file — only its last four characters while the wallet is locked. */
  bankIban: string | null;
  /** `bankIban` is the masked form ("•••• 1234"): never a value to save back. */
  bankIbanMasked?: boolean;
  bankSwift: string | null;
  currency: string | null;
  businessName: string | null;
  vatNumber: string | null;
  registrationNumber: string | null;
  fiscalType: FiscalTypeCode | null;
  selfBillingAccepted: boolean;
  selfBillingAcceptedAt: string | null;
}

interface Payout {
  id: string;
  type: string;
  amount: number;
  currency: string;
  invoiceNumber: string | null;
  invoiceUrl: string | null;
  invoiceDate: string | null;
  status: string;
  paidAt: string | null;
  createdAt: string;
}

interface PayoutsResponse {
  balance: WalletBalance;
  payoutInfo: PayoutInfoData;
  payouts: Payout[];
}

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

interface WalletSearch {
  tab: TabId;
}

// Shared overview cache key — every panel reuses the same balance snapshot.
const OVERVIEW_KEY = ["api", "/api/account/wallet", "overview"] as const;

const overviewQueryOptions = {
  queryKey: OVERVIEW_KEY,
  queryFn: () => apiFetch.get<OverviewResponse>("/api/account/wallet?tab=overview"),
} as const;

export const Route = createFileRoute("/_authenticated/account/user/wallet")({
  validateSearch: (search: Record<string, unknown>): WalletSearch => {
    const raw = typeof search.tab === "string" ? search.tab : "";
    return { tab: VALID_TABS.includes(raw as TabId) ? (raw as TabId) : "overview" };
  },
  // Prefetch overview snapshot (balance + referral status) so the header
  // renders with data on the first paint instead of flashing a skeleton.
  loader: ({ context }) => context.queryClient.ensureQueryData(overviewQueryOptions),
  component: WalletPage,
});

function WalletPage() {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const { tab: activeTab } = Route.useSearch();
  const navigate = useNavigate();
  const swipeRef = useRef<SwipeableTabsHandle>(null);

  // We always fetch the overview eagerly so the header can reflect referral
  // status without waiting for the tab content. Route loader has already
  // primed the cache so this hits warm on first mount.
  const overviewQuery = useQuery(overviewQueryOptions);

  const balance = overviewQuery.data?.balance ?? null;

  // Country-level referral program flag — same source as side-nav's wallet
  // entry (`/api/account/sidebar-data`, prefetched by the _authenticated
  // loader). Defaults to enabled while loading so tabs don't flash out on
  // the common (enabled) path; legacy reads useCountry().referralEnabled.
  const sidebarQuery = useQuery(sidebarDataQueryOptions);
  const countryReferralEnabled = sidebarQuery.data?.countryReferralEnabled !== false;

  const refreshBalance = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["api", "/api/account/wallet"] });
  }, [queryClient]);

  const toggleReferralMutation = useMutation({
    mutationFn: (next: boolean) => {
      const toast = showToast(t`Saving…`, "loading");
      return apiFetch
        .post("/api/account/wallet/toggle-referral", { enabled: next })
        .then((res) => {
          toast.update(t`Saved`, "success");
          return res;
        })
        .catch((err) => {
          toast.update(t`Could not save changes.`, "error");
          throw err;
        });
    },
    onSuccess: () => {
      refreshBalance();
      dispatchReferralToggled();
    },
  });

  const setTab = useCallback(
    (next: TabId) => {
      navigate({ to: "/account/user/wallet", search: { tab: next } });
    },
    [navigate],
  );

  const allTabs: TabItem<TabId>[] = [
    { id: "overview", label: t`Overview`, icon: <LayoutDashboard className="size-4" /> },
    { id: "referrals", label: t`Referrals`, icon: <Users className="size-4" /> },
    { id: "commissions", label: t`Commissions`, icon: <TrendingUp className="size-4" /> },
    { id: "wallet", label: t`Wallet`, icon: <WalletIcon className="size-4" /> },
    { id: "payouts", label: t`Payouts`, icon: <Banknote className="size-4" /> },
  ];
  const tabs = countryReferralEnabled
    ? allTabs
    : allTabs.filter((tab) => !REFERRAL_ONLY_TABS.includes(tab.id));
  const visibleTabIds = tabs.map((tab) => tab.id);
  // Clamp deep links to hidden tabs back to overview (legacy parity).
  const effectiveTab: TabId = visibleTabIds.includes(activeTab) ? activeTab : "overview";

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="font-display text-foreground text-2xl font-semibold tracking-[-0.02em]">
            <Trans>Wallet</Trans>
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            <Trans>Earnings, referrals, and payouts.</Trans>
          </p>
        </div>
        {countryReferralEnabled && (
          /* The label used to be `hidden sm:inline`, leaving a bare unlabelled
             switch on mobile — the hardest thing to find in the whole referral
             flow (_docs/product-analysis-referrals-subscriptions-2026-09-10.md
             §3.1). Now: label + one explaining sentence at EVERY width, the
             whole thing one 44px-tall <label> tap target, and its own row on
             mobile so it never squeezes the page title. */
          <div className="border-border bg-muted/40 flex items-center justify-between gap-3 rounded-xl border p-3 sm:max-w-[16rem] sm:border-0 sm:bg-transparent sm:p-0">
            <label
              htmlFor="referral-toggle-header"
              className="flex min-h-[44px] cursor-pointer flex-col justify-center select-none"
            >
              {/* The switch is named by these two spans (aria-labelledby /
                  -describedby) rather than a hardcoded aria-label, so the
                  announced name carries the STATE word the user can see
                  ("Referral program active" / "… off"). */}
              <span id="referral-toggle-label" className="text-foreground text-xs font-medium">
                {balance?.referralEnabled ? (
                  <Trans>Referral program active</Trans>
                ) : (
                  <Trans>Referral program off</Trans>
                )}
              </span>
              <span
                id="referral-toggle-hint"
                className="text-muted-foreground text-[0.6875rem] leading-snug"
              >
                {balance?.referralEnabled ? (
                  <Trans>Your link earns commission. You can turn it off any time.</Trans>
                ) : (
                  <Trans>Turn it on to get your link and earn commission.</Trans>
                )}
              </span>
            </label>
            <div className="flex min-h-[44px] shrink-0 items-center">
              <Switch
                id="referral-toggle-header"
                aria-labelledby="referral-toggle-label"
                aria-describedby="referral-toggle-hint"
                checked={balance?.referralEnabled ?? false}
                onCheckedChange={() =>
                  balance && toggleReferralMutation.mutate(!balance.referralEnabled)
                }
                disabled={!balance || toggleReferralMutation.isPending}
              />
            </div>
          </div>
        )}
      </header>

      <TabBar
        tabs={tabs}
        activeTab={effectiveTab}
        onTabChange={setTab}
        onActiveTabClick={() => swipeRef.current?.scrollActiveToTop()}
        className="mt-6"
      />

      <SwipeableTabsContent
        ref={swipeRef}
        activeTab={effectiveTab}
        tabIds={visibleTabIds}
        onTabChange={setTab}
        className="mt-4"
        renderPanel={(id) => {
          let inner: React.ReactNode = null;
          if (id === "overview")
            inner = <OverviewPanel countryReferralEnabled={countryReferralEnabled} />;
          else if (id === "referrals") inner = <ReferralsPanel balance={balance} />;
          else if (id === "commissions") inner = <CommissionsPanel balance={balance} />;
          else if (id === "wallet") inner = <WalletPanel balance={balance} />;
          else if (id === "payouts")
            inner = <PayoutsPanel balance={balance} onRefresh={refreshBalance} />;
          if (!inner) return null;
          // OverviewPanel already renders its own AccountSecurityBanner
          // inline at the bottom — keep that for the recent-activity card
          // continuity; wrap the other panels here.
          if (id === "overview") return inner;
          return (
            <div className="space-y-6">
              {inner}
              <AccountSecurityBanner />
            </div>
          );
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview panel
// ---------------------------------------------------------------------------

/** Two-decimal money in the UI locale (P17): "12,50 €" in ro, "€12.50" in en. */
const MONEY_2 = { minimumFractionDigits: 2, maximumFractionDigits: 2 } as const;

/** Localized month label for a 1-based month number. */
function monthLabel(month1: number, locale: string, style: "short" | "long" = "short"): string {
  return new Intl.DateTimeFormat(locale, { month: style }).format(new Date(2000, month1 - 1, 1));
}

function OverviewPanel({ countryReferralEnabled }: { countryReferralEnabled: boolean }) {
  const { t, i18n } = useLingui();
  const { country } = useBrowsingCountry();
  const countryCode = (country ?? "ro").toLowerCase();

  const query = useQuery({
    queryKey: [...OVERVIEW_KEY, countryCode],
    queryFn: () =>
      apiFetch.get<OverviewResponse>(
        `/api/account/wallet?tab=overview&country=${encodeURIComponent(countryCode)}`,
      ),
  });

  if (query.isLoading) return <OverviewSkeleton />;
  if (query.isError) return <ErrorState onRetry={() => query.refetch()} />;

  const { balance, stats, projection, recentActivity, countryPricing } = query.data!;
  const fmt = (n: number) => formatMoney(n, balance.currency || "EUR", i18n.locale, MONEY_2);
  const fmtAmount = (amount: number) =>
    amount >= 1000 ? `${(amount / 1000).toFixed(1)}k` : amount.toFixed(0);

  const projectionTotal = projection.reduce((sum, p) => sum + p.amount, 0);
  const showProjection = projection.length > 0 && projection.some((p) => p.amount > 0);

  return (
    <div className="space-y-5 pb-6">
      {/* Balance Card */}
      <m.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-primary-soft border-primary/20 rounded-xl border p-6"
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="text-secondary-foreground flex items-center gap-2">
              <WalletIcon className="size-4" aria-hidden />
              <span className="text-sm font-medium">
                <Trans>Wallet</Trans>
              </span>
            </div>
            <div className="font-display text-foreground mt-2 text-3xl font-bold tabular-nums">
              {fmt(balance.available)}
            </div>
            <div className="text-secondary-foreground mt-1 text-sm">
              <Trans>Available balance</Trans>
            </div>
            {(balance.subscriptionCredit ?? 0) > 0 && (
              <div className="text-secondary-foreground mt-1 text-xs">
                <Trans>
                  + {fmt(balance.subscriptionCredit ?? 0)} subscription credit (renewals only)
                </Trans>
              </div>
            )}
          </div>
          {balance.hasPremium && (
            <Badge className="bg-card text-secondary-foreground">
              <Trans>Plus</Trans>
            </Badge>
          )}
        </div>
      </m.div>

      {/* Stat Cards — only when the country referral program is enabled */}
      {countryReferralEnabled && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {[
            {
              label: t`Total referrals`,
              value: stats.totalReferrals.toString(),
              icon: <Users className="size-4" />,
            },
            {
              label: t`Active referrals`,
              value: stats.activeReferrals.toString(),
              icon: <UserCheck className="size-4" />,
            },
            {
              label: t`Link views`,
              value: stats.linkClicks.toString(),
              icon: <MousePointerClick className="size-4" />,
            },
            {
              label: t`Pending commissions`,
              value: fmt(stats.pendingCommissions),
              icon: <Clock className="size-4" />,
            },
            {
              label: t`Total earned`,
              value: fmt(stats.totalEarned),
              icon: <TrendingUp className="size-4" />,
            },
          ].map((stat, i) => (
            <m.div
              key={stat.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-card rounded-xl border p-4"
            >
              <div className="text-muted-foreground mb-1 flex items-center gap-1.5 text-xs">
                {stat.icon}
                {stat.label}
              </div>
              <div className="text-foreground text-lg font-bold">{stat.value}</div>
            </m.div>
          ))}
        </div>
      )}

      {/* Referral share banner */}
      {countryReferralEnabled && balance.referralCode && <ShareReferralBanner />}

      {/* 12-month projection */}
      {countryReferralEnabled && showProjection && (
        <m.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-card rounded-xl border"
        >
          <div className="border-b px-4 py-3">
            <div className="flex items-center gap-2">
              <CalendarRange className="text-muted-foreground size-4" />
              <h2 className="text-sm font-semibold">
                <Trans>12-month projection</Trans>
              </h2>
            </div>
            <p className="text-muted-foreground mt-0.5 text-xs">
              <Trans>Estimated commissions if all current referrals stay active.</Trans>
            </p>
          </div>

          <div className="px-4 py-4">
            <div className="flex items-end gap-1.5" style={{ height: 120 }}>
              {(() => {
                const maxAmount = Math.max(...projection.map((p) => p.amount), 1);
                return projection.map((p, i) => {
                  const heightPct = (p.amount / maxAmount) * 100;
                  const [, mm] = p.month.split("-");
                  return (
                    <div key={p.month} className="flex flex-1 flex-col items-center gap-1">
                      <span className="text-foreground text-[0.6rem] font-medium">
                        {p.amount > 0 ? fmtAmount(p.amount) : ""}
                      </span>
                      <m.div
                        initial={{ height: 0 }}
                        animate={{ height: `${Math.max(heightPct, 2)}%` }}
                        transition={{ delay: i * 0.04, duration: 0.3 }}
                        className={`w-full rounded-t ${p.amount > 0 ? "bg-teal-500 dark:bg-teal-600" : "bg-muted"}`}
                        style={{ minHeight: 2 }}
                      />
                      <span className="text-muted-foreground text-[0.6rem]">
                        {monthLabel(Number(mm), i18n.locale)}
                      </span>
                    </div>
                  );
                });
              })()}
            </div>

            <div className="mt-3 flex items-center justify-between border-t pt-3">
              <span className="text-muted-foreground text-xs font-medium">
                <Trans>12-month total</Trans>
              </span>
              <span className="text-foreground text-sm font-bold">{fmt(projectionTotal)}</span>
            </div>
          </div>
        </m.div>
      )}

      {/* Earnings calculator */}
      {countryReferralEnabled && (
        <m.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.28 }}
          className="bg-card rounded-xl border p-5"
        >
          <h2 className="text-foreground mb-4 text-sm font-semibold">
            <Trans>Earnings calculator</Trans>
          </h2>
          <ReferralEarningsCalculator
            monthlyPrice={countryPricing?.monthlyPrice ?? 54}
            currencySymbol={countryPricing?.currencySymbol ?? "€"}
            currencyBefore={countryPricing?.currencyBefore ?? true}
            rates={commissionRates(balance.referralTerms)}
          />
        </m.div>
      )}

      {/* Wallet uses */}
      <m.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-card rounded-xl border p-5"
      >
        <h2 className="mb-3 text-sm font-semibold">
          <Trans>What you can do with your balance</Trans>
        </h2>
        <div className="grid gap-3 sm:grid-cols-3">
          <WalletUseCard
            icon={<Banknote className="size-5" />}
            title={t`Cash out`}
            desc={t`Request a payout to your bank account.`}
          />
          <WalletUseCard
            icon={<CreditCard className="size-5" />}
            title={t`Pay subscriptions`}
            desc={t`Use your balance toward your next subscription invoice.`}
          />
          <WalletUseCard
            icon={<Stethoscope className="size-5" />}
            title={t`Pay for consultations`}
            desc={t`Apply credits to upcoming appointments.`}
            soon
            soonLabel={t`Soon`}
          />
        </div>
      </m.div>

      {/* Recent activity */}
      <m.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
        className="bg-card rounded-xl border"
      >
        <div className="border-b px-4 py-3">
          <h2 className="text-sm font-semibold">
            <Trans>Recent activity</Trans>
          </h2>
        </div>
        {recentActivity.length > 0 ? (
          <div className="divide-y">
            {recentActivity.map((item) => (
              <div key={item.id} className="flex items-center gap-3 px-4 py-3">
                <div
                  className={cn(
                    "flex size-8 items-center justify-center rounded-full",
                    item.amount > 0
                      ? "bg-green-100 text-green-600 dark:bg-green-950 dark:text-green-400"
                      : "bg-red-100 text-red-600 dark:bg-red-950 dark:text-red-400",
                  )}
                >
                  {item.amount > 0 ? (
                    <ArrowDownLeft className="size-3.5" />
                  ) : (
                    <ArrowUpRight className="size-3.5" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-foreground truncate text-sm">{item.description}</p>
                  <p className="text-muted-foreground text-xs">
                    {new Date(item.createdAt).toLocaleDateString(i18n.locale)}
                    {item.originalCurrency && item.originalAmount != null && (
                      <span className="text-muted-foreground/70">
                        {" · "}
                        {formatMoney(
                          item.originalAmount,
                          item.originalCurrency,
                          i18n.locale,
                          MONEY_2,
                        )}{" "}
                        → {item.currency}
                      </span>
                    )}
                  </p>
                </div>
                <div className="text-right">
                  <p
                    className={cn(
                      "text-sm font-medium",
                      item.amount > 0
                        ? "text-green-600 dark:text-green-400"
                        : "text-red-600 dark:text-red-400",
                    )}
                  >
                    {item.amount > 0 ? "+" : ""}
                    {formatMoney(item.amount, item.currency, i18n.locale, MONEY_2)}
                  </p>
                  {item.originalCurrency && item.originalAmount != null && (
                    <p className="text-muted-foreground/70 text-[0.625rem]">
                      {formatMoney(
                        item.originalAmount,
                        item.originalCurrency,
                        i18n.locale,
                        MONEY_2,
                      )}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-4 py-8 text-center">
            <p className="text-muted-foreground text-sm">
              <Trans>No recent activity yet.</Trans>
            </p>
          </div>
        )}
      </m.div>

      <AccountSecurityBanner />
    </div>
  );
}

function WalletUseCard({
  icon,
  title,
  desc,
  soon,
  soonLabel,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  soon?: boolean;
  soonLabel?: string;
}) {
  return (
    <div className={cn("flex items-start gap-3 rounded-lg border p-4", soon && "opacity-60")}>
      <div className="text-primary mt-0.5 shrink-0">{icon}</div>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-foreground text-sm font-medium">{title}</p>
          {soon && (
            <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[0.6rem] leading-none font-semibold text-amber-600 uppercase dark:text-amber-400">
              {soonLabel}
            </span>
          )}
        </div>
        <p className="text-muted-foreground mt-0.5 text-xs">{desc}</p>
      </div>
    </div>
  );
}

function ShareReferralBanner() {
  // The banner only triggers the share sheet (mounted at root). The link
  // itself is built inside <ReferralShareSheet> from useReferralProgram —
  // no referralCode needed here. The wallet-page gate already verifies the
  // user has one before rendering this banner.
  const { openReferralShare } = useReferralShare();

  return (
    <m.button
      type="button"
      onClick={() => openReferralShare({ source: "wallet-overview" })}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      className="group bg-primary-soft border-primary/20 relative w-full overflow-hidden rounded-xl border p-5 text-left transition-shadow hover:shadow-md"
    >
      <div className="relative flex items-center gap-4">
        <div className="relative shrink-0">
          <span className="bg-primary text-primary-foreground flex size-12 items-center justify-center rounded-xl">
            <Gift className="size-6" aria-hidden />
          </span>
          <m.span
            initial={false}
            animate={{ y: [0, -2, 0] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            className="border-primary-soft bg-card text-secondary-foreground absolute -right-1.5 -bottom-1.5 flex size-6 items-center justify-center rounded-full border-2"
          >
            <Send className="size-3" aria-hidden />
          </m.span>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <p className="text-foreground line-clamp-2 text-sm font-semibold">
              <Trans>Share your referral link</Trans>
            </p>
            <span className="bg-card text-secondary-foreground shrink-0 rounded-full px-2 py-0.5 text-[0.625rem] font-semibold">
              <Trans>Earn a commission</Trans>
            </span>
          </div>
          <p className="text-secondary-foreground mt-0.5 line-clamp-2 text-xs">
            <Trans>Tap to share with doctors and clinics — up to 3 years of commission.</Trans>
          </p>
        </div>

        <m.span
          initial={false}
          animate={{ x: 0 }}
          whileHover={{ x: 3 }}
          className="text-secondary-foreground shrink-0"
        >
          <ArrowRight className="size-5" aria-hidden />
        </m.span>
      </div>
    </m.button>
  );
}

interface CommissionRates {
  /** Percent of the referred doctor's subscription, per year (e.g. 20 = 20%). */
  y1: number;
  y2: number;
  y3: number;
}

/**
 * Commission percentages from the user's referral terms snapshot (frozen per
 * country from `Country.features.referral_commission_y1..y3` when the program
 * was activated — apps/api lib/referrals.ts). Falls back to the platform
 * defaults, 20/10/5, the same defaults the API uses.
 */
function commissionRates(terms: Record<string, unknown> | null): CommissionRates {
  const pick = (key: string, fallback: number) => {
    const value = Number(terms?.[key]);
    return Number.isFinite(value) && value >= 0 ? value : fallback;
  };
  return {
    y1: pick("commission_y1", 20),
    y2: pick("commission_y2", 10),
    y3: pick("commission_y3", 5),
  };
}

function ReferralEarningsCalculator({
  monthlyPrice,
  currencySymbol,
  currencyBefore,
  rates,
  defaultProviders = 10,
  maxProviders = 100,
}: {
  monthlyPrice: number;
  currencySymbol: string;
  currencyBefore: boolean;
  rates: CommissionRates;
  defaultProviders?: number;
  maxProviders?: number;
}) {
  const { t, i18n } = useLingui();
  const [providers, setProviders] = useState(defaultProviders);

  const yearly = (percent: number) => Math.round(providers * monthlyPrice * (percent / 100) * 12);
  const y1 = yearly(rates.y1);
  const y2 = yearly(rates.y2);
  const y3 = yearly(rates.y3);
  const total = y1 + y2 + y3;
  const rateY1 = rates.y1.toLocaleString(i18n.locale);
  const rateY2 = rates.y2.toLocaleString(i18n.locale);
  const rateY3 = rates.y3.toLocaleString(i18n.locale);

  const fmt = (n: number) => {
    const formatted = n.toLocaleString(i18n.locale);
    return currencyBefore ? `${currencySymbol}${formatted}` : `${formatted} ${currencySymbol}`;
  };

  const cards = [
    { label: t`Year 1 (${rateY1}%)`, amount: y1, isTotal: false },
    { label: t`Year 2 (${rateY2}%)`, amount: y2, isTotal: false },
    { label: t`Year 3 (${rateY3}%)`, amount: y3, isTotal: false },
    { label: t`3-year total`, amount: total, isTotal: true },
  ];

  return (
    <div>
      <div className="mb-6">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-sm font-medium">
            <Trans>Doctors you refer</Trans>
          </span>
          <span className="bg-primary/10 text-primary rounded-full px-3 py-0.5 text-sm font-bold tabular-nums">
            {providers}
          </span>
        </div>
        <input
          type="range"
          min={1}
          max={maxProviders}
          step={1}
          value={providers}
          onChange={(e) => setProviders(Number(e.target.value))}
          className="accent-primary w-full"
          aria-label={t`Doctors you refer`}
        />
        <div className="text-muted-foreground mt-1 flex justify-between text-xs">
          <span>1</span>
          <span>{maxProviders}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {cards.map(({ label, amount, isTotal }) => (
          <m.div
            key={label}
            layout
            animate={{ scale: [1, 1.04, 1] }}
            transition={{ duration: 0.2 }}
            className={cn(
              "flex flex-col items-center rounded-xl border p-4 text-center",
              isTotal
                ? "border-primary/30 bg-primary/5 ring-primary/20 ring-2"
                : "border-border/50 bg-card",
            )}
          >
            <span
              className={cn(
                "text-2xl font-bold tabular-nums",
                isTotal ? "text-primary" : "text-foreground",
              )}
            >
              {fmt(amount)}
            </span>
            <span className="text-muted-foreground mt-1.5 text-xs leading-snug">{label}</span>
          </m.div>
        ))}
      </div>
    </div>
  );
}

function OverviewSkeleton() {
  return (
    <div className="space-y-5 pb-6">
      <Skeleton className="h-40 w-full rounded-xl" />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-20 rounded-xl" />
        ))}
      </div>
      <Skeleton className="h-32 w-full rounded-xl" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Referrals panel
// ---------------------------------------------------------------------------

function ReferralsPanel({ balance }: { balance: WalletBalance | null }) {
  const { t, i18n } = useLingui();
  const { openReferralShare } = useReferralShare();
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["api", "/api/account/wallet", "referrals", page],
    queryFn: () =>
      apiFetch.get<ReferralsResponse>(`/api/account/wallet?tab=referrals&page=${page}`),
  });

  if (query.isLoading) return <ListSkeleton />;
  if (query.isError) return <ErrorState onRetry={() => query.refetch()} />;

  const { referrals, pagination } = query.data!;
  const fmt = (n: number) => formatMoney(n, balance?.currency || "EUR", i18n.locale, MONEY_2);

  const statusConfig: Record<string, { color: string; label: string }> = {
    pending: {
      color: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400",
      label: t`Pending`,
    },
    active: {
      color: "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400",
      label: t`Active`,
    },
    churned: {
      color: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400",
      label: t`Churned`,
    },
    expired: { color: "bg-muted text-muted-foreground", label: t`Expired` },
  };

  const typeLabels: Record<string, string> = {
    patient: t`Patient`,
    provider: t`Doctor`,
    facility: t`Facility`,
    business: t`Business`,
  };

  if (referrals.length === 0) {
    return (
      <div className="bg-card flex flex-col items-center gap-4 rounded-xl border px-4 py-12 text-center">
        <span className="bg-muted flex size-14 items-center justify-center rounded-2xl">
          <Users className="text-muted-foreground size-7" />
        </span>
        <p className="text-muted-foreground max-w-xs text-sm">
          <Trans>No referrals yet — share your link to start earning.</Trans>
        </p>
        <Button
          size="sm"
          className="min-h-[44px]"
          onClick={() => openReferralShare({ source: "wallet-referrals-empty" })}
        >
          <Send className="mr-1.5 size-4" />
          <Trans>Share now</Trans>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3 pb-6">
      <div className="text-muted-foreground hidden border-b px-4 pb-2 text-xs font-medium md:grid md:grid-cols-6 md:gap-2">
        <div>
          <Trans>Name</Trans>
        </div>
        <div>
          <Trans>Type</Trans>
        </div>
        <div>
          <Trans>Joined</Trans>
        </div>
        <div>
          <Trans>Status</Trans>
        </div>
        <div>
          <Trans>Plan</Trans>
        </div>
        <div className="text-right">
          <Trans>Commission</Trans>
        </div>
      </div>

      {referrals.map((ref, i) => {
        const sc = statusConfig[ref.status] ?? statusConfig.pending;
        const isExpanded = expandedId === ref.id;
        const currentYear = getCurrentCommissionYear(ref.commissionStartDate);
        const currentPct =
          currentYear === 1
            ? ref.commissionY1Pct
            : currentYear === 2
              ? ref.commissionY2Pct
              : ref.commissionY3Pct;
        const monthlyCommission =
          ref.monthlyValue && ref.status === "active" ? (ref.monthlyValue * currentPct) / 100 : 0;

        return (
          <m.div
            key={ref.id}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.03 }}
          >
            <button
              onClick={() => setExpandedId(isExpanded ? null : ref.id)}
              className="bg-card hover:bg-muted/50 w-full rounded-xl border p-4 text-left transition-colors"
            >
              {/* Mobile */}
              <div className="flex items-center justify-between md:hidden">
                <div className="min-w-0 flex-1">
                  <p className="text-foreground truncate text-sm font-medium">{ref.name}</p>
                  <div className="mt-1 flex items-center gap-2">
                    <Badge variant="outline" className="text-[0.625rem]">
                      {typeLabels[ref.type] ?? ref.type}
                    </Badge>
                    <Badge className={`text-[0.625rem] ${sc.color}`}>{sc.label}</Badge>
                  </div>
                </div>
                <ChevronDown
                  className={cn(
                    "text-muted-foreground size-4 transition-transform",
                    isExpanded && "rotate-180",
                  )}
                />
              </div>

              {/* Desktop */}
              <div className="hidden items-center md:grid md:grid-cols-6 md:gap-2">
                <div className="text-foreground truncate text-sm font-medium">{ref.name}</div>
                <div>
                  <Badge variant="outline" className="text-[0.625rem]">
                    {typeLabels[ref.type] ?? ref.type}
                  </Badge>
                </div>
                <div className="text-muted-foreground text-sm">
                  {new Date(ref.createdAt).toLocaleDateString(i18n.locale)}
                </div>
                <div>
                  <Badge className={`text-[0.625rem] ${sc.color}`}>{sc.label}</Badge>
                </div>
                <div className="text-muted-foreground text-sm">{ref.planTier ?? "—"}</div>
                <div className="text-right text-sm font-medium">
                  {monthlyCommission > 0 ? `${fmt(monthlyCommission)} (${currentPct}%)` : "—"}
                </div>
              </div>
            </button>

            <AnimatePresence>
              {isExpanded && (
                <m.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="bg-muted/30 space-y-2 rounded-b-xl border-x border-b px-4 py-3 text-sm">
                    <DetailRow
                      label={t`Joined`}
                      value={new Date(ref.createdAt).toLocaleDateString(i18n.locale)}
                    />
                    <DetailRow label={t`Type`} value={typeLabels[ref.type] ?? ref.type} />
                    <DetailRow label={t`Plan`} value={ref.planTier ?? "—"} />
                    {ref.monthlyValue != null && (
                      <DetailRow label={t`Monthly value`} value={fmt(ref.monthlyValue)} />
                    )}
                    <DetailRow
                      label={t`Year 1`}
                      value={`${ref.commissionY1Pct}%${currentYear === 1 ? " ●" : ""}`}
                    />
                    <DetailRow
                      label={t`Year 2`}
                      value={`${ref.commissionY2Pct}%${currentYear === 2 ? " ●" : ""}`}
                    />
                    <DetailRow
                      label={t`Year 3`}
                      value={`${ref.commissionY3Pct}%${currentYear === 3 ? " ●" : ""}`}
                    />
                    <DetailRow label={t`Total earned`} value={fmt(ref.totalCommission)} />
                  </div>
                </m.div>
              )}
            </AnimatePresence>
          </m.div>
        );
      })}

      <Pagination
        page={page}
        total={pagination.total}
        limit={pagination.limit}
        onPageChange={setPage}
      />
    </div>
  );
}

function getCurrentCommissionYear(startDate: string): number {
  const start = new Date(startDate);
  const now = new Date();
  const months =
    (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (months < 12) return 1;
  if (months < 24) return 2;
  return 3;
}

// ---------------------------------------------------------------------------
// Commissions panel
// ---------------------------------------------------------------------------

function CommissionsPanel({ balance }: { balance: WalletBalance | null }) {
  const { t, i18n } = useLingui();
  const [page, setPage] = useState(1);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["api", "/api/account/wallet", "commissions", page],
    queryFn: () =>
      apiFetch.get<CommissionsResponse>(`/api/account/wallet?tab=commissions&page=${page}`),
  });

  if (query.isLoading) return <ListSkeleton />;
  if (query.isError) return <ErrorState onRetry={() => query.refetch()} />;

  const { commissions, pagination } = query.data!;
  const fmt = (n: number) => formatMoney(n, balance?.currency || "EUR", i18n.locale, MONEY_2);
  const fmtOriginal = (amount: number, currency: string) =>
    formatMoney(amount, currency, i18n.locale, MONEY_2);

  const statusConfig: Record<string, { color: string; label: string }> = {
    pending: {
      color: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400",
      label: t`Pending`,
    },
    approved: {
      color: "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400",
      label: t`Approved`,
    },
    paid: {
      color: "bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-400",
      label: t`Paid`,
    },
    cancelled: {
      color: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400",
      label: t`Cancelled`,
    },
  };

  if (commissions.length === 0) {
    return (
      <div className="bg-card rounded-xl border px-4 py-12 text-center">
        <TrendingUp className="text-muted-foreground mx-auto mb-3 size-10" />
        <p className="text-muted-foreground text-sm">
          <Trans>No commissions yet.</Trans>
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 pb-6">
      <div className="text-muted-foreground hidden border-b px-4 pb-2 text-xs font-medium md:grid md:grid-cols-5 md:gap-2">
        <div>
          <Trans>Period</Trans>
        </div>
        <div>
          <Trans>Active referrals</Trans>
        </div>
        <div>
          <Trans>Subscriptions</Trans>
        </div>
        <div>
          <Trans>Commission</Trans>
        </div>
        <div className="text-right">
          <Trans>Status</Trans>
        </div>
      </div>

      {commissions.map((group, i) => {
        const key = `${group.year}-${group.month}`;
        const isExpanded = expandedKey === key;
        const sc = statusConfig[group.status] ?? statusConfig.pending;

        return (
          <m.div
            key={key}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.03 }}
          >
            <button
              onClick={() => setExpandedKey(isExpanded ? null : key)}
              className="bg-card hover:bg-muted/50 w-full rounded-xl border p-4 text-left transition-colors"
            >
              {/* Mobile */}
              <div className="flex items-center justify-between md:hidden">
                <div>
                  <p className="text-foreground text-sm font-medium">
                    {new Intl.DateTimeFormat(i18n.locale, {
                      month: "short",
                      year: "numeric",
                    }).format(new Date(group.year, group.month - 1, 1))}
                  </p>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-muted-foreground text-xs">
                      <Trans>{group.activeReferrals} active</Trans>
                    </span>
                    <Badge className={`text-[0.625rem] ${sc.color}`}>{sc.label}</Badge>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-foreground text-sm font-bold">
                    {fmt(group.totalCommission)}
                  </span>
                  <ChevronDown
                    className={cn(
                      "text-muted-foreground size-4 transition-transform",
                      isExpanded && "rotate-180",
                    )}
                  />
                </div>
              </div>

              {/* Desktop */}
              <div className="hidden items-center md:grid md:grid-cols-5 md:gap-2">
                <div className="text-foreground text-sm font-medium">
                  {new Intl.DateTimeFormat(i18n.locale, { month: "short", year: "numeric" }).format(
                    new Date(group.year, group.month - 1, 1),
                  )}
                </div>
                <div className="text-muted-foreground text-sm">{group.activeReferrals}</div>
                <div className="text-muted-foreground text-sm">{fmt(group.totalSubscription)}</div>
                <div className="text-foreground text-sm font-bold">
                  {fmt(group.totalCommission)}
                </div>
                <div className="flex items-center justify-end gap-2">
                  <Badge className={`text-[0.625rem] ${sc.color}`}>{sc.label}</Badge>
                  <ChevronDown
                    className={cn(
                      "text-muted-foreground size-4 transition-transform",
                      isExpanded && "rotate-180",
                    )}
                  />
                </div>
              </div>
            </button>

            <AnimatePresence>
              {isExpanded && (
                <m.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="bg-muted/30 divide-y rounded-b-xl border-x border-b">
                    {group.items.map((item) => {
                      const itemSc = statusConfig[item.status] ?? statusConfig.pending;
                      return (
                        <div
                          key={item.id}
                          className="flex items-center justify-between px-4 py-2.5"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-foreground truncate text-sm">{item.referralName}</p>
                            <p className="text-muted-foreground text-xs">
                              {item.planTier ?? "—"} · {fmt(item.subscriptionAmount)} ×{" "}
                              {item.commissionPct}% (<Trans>Year {item.commissionYear}</Trans>)
                            </p>
                            {item.originalCurrency && item.originalSubscriptionAmount != null && (
                              <p className="text-muted-foreground/70 text-[0.625rem]">
                                <Trans>
                                  Converted from{" "}
                                  {fmtOriginal(
                                    item.originalSubscriptionAmount,
                                    item.originalCurrency,
                                  )}{" "}
                                  @ {item.exchangeRateUsed?.toFixed(4) ?? ""}
                                </Trans>
                              </p>
                            )}
                          </div>
                          <div className="ml-3 flex flex-col items-end gap-0.5">
                            <div className="flex items-center gap-2">
                              <span className="text-foreground text-sm font-medium">
                                {fmt(item.commissionAmount)}
                              </span>
                              <Badge className={`text-[0.625rem] ${itemSc.color}`}>
                                {itemSc.label}
                              </Badge>
                            </div>
                            {item.originalCurrency && item.originalCommissionAmount != null && (
                              <span className="text-muted-foreground/70 text-[0.625rem]">
                                {fmtOriginal(item.originalCommissionAmount, item.originalCurrency)}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </m.div>
              )}
            </AnimatePresence>
          </m.div>
        );
      })}

      <Pagination
        page={page}
        total={pagination.total}
        limit={pagination.limit}
        onPageChange={setPage}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Wallet (transactions) panel
// ---------------------------------------------------------------------------

function WalletPanel({ balance }: { balance: WalletBalance | null }) {
  const { t, i18n } = useLingui();
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: ["api", "/api/account/wallet", "wallet", page],
    queryFn: () =>
      apiFetch.get<TransactionsResponse>(`/api/account/wallet?tab=wallet&page=${page}`),
  });

  if (query.isLoading) return <ListSkeleton />;
  if (query.isError) return <ErrorState onRetry={() => query.refetch()} />;

  const { transactions, wallet, pagination } = query.data!;
  const fmt = (n: number) => formatMoney(n, balance?.currency || "EUR", i18n.locale, MONEY_2);

  const typeLabels: Record<string, string> = {
    commission_credit: t`Commission credit`,
    subscription_debit: t`Subscription paid`,
    // A wallet payment given back after the card / Stripe refused it.
    subscription_debit_reversal: t`Subscription payment returned`,
    // Credit for the unused part of a plan (proration) — rows written before
    // 2026-10; proration now goes to the subscription credit below.
    subscription_refund: t`Subscription refund`,
    // The subscription-only credit (proration): spent on renewals, never paid out.
    subscription_credit: t`Subscription credit (renewals only)`,
    subscription_credit_used: t`Subscription credit used for a renewal`,
    // Wallet credit moved onto a Stripe invoice before it is charged.
    subscription_deduction: t`Applied to an invoice`,
    account_merge_credit: t`Balance moved from a merged account`,
    account_merge_debit: t`Balance moved to the account this one was merged into`,
    payout_debit: t`Payout`,
    service_debit: t`Service charge`,
    manual_credit: t`Manual credit`,
    manual_debit: t`Manual debit`,
  };

  return (
    <div className="space-y-5 pb-6">
      {/* Balance summary */}
      <div className="bg-card grid grid-cols-2 gap-3 rounded-xl border p-4 sm:grid-cols-4">
        <SummaryStat label={t`Available`} value={fmt(balance?.available ?? 0)} />
        <SummaryStat label={t`Pending`} value={fmt(wallet?.pending ?? 0)} />
        <SummaryStat label={t`Subscription credit`} value={fmt(balance?.subscriptionCredit ?? 0)} />
        <SummaryStat label={t`Total earned`} value={fmt(wallet?.totalEarned ?? 0)} />
      </div>

      {transactions.length > 0 ? (
        <>
          <div className="text-muted-foreground hidden border-b px-4 pb-2 text-xs font-medium md:grid md:grid-cols-5 md:gap-2">
            <div>
              <Trans>Date</Trans>
            </div>
            <div className="col-span-2">
              <Trans>Description</Trans>
            </div>
            <div className="text-right">
              <Trans>Amount</Trans>
            </div>
            <div className="text-right">
              <Trans>Balance</Trans>
            </div>
          </div>

          <div className="space-y-1">
            {transactions.map((tx, i) => {
              const isCredit = tx.amount > 0;
              return (
                <m.div
                  key={tx.id}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.02 }}
                  className="bg-card hover:bg-muted/50 rounded-lg border p-3 transition-colors"
                >
                  {/* Mobile */}
                  <div className="flex items-center gap-3 md:hidden">
                    <TxIcon credit={isCredit} />
                    <div className="min-w-0 flex-1">
                      <p className="text-foreground truncate text-sm">{tx.description}</p>
                      <p className="text-muted-foreground text-xs">
                        {new Date(tx.createdAt).toLocaleDateString(i18n.locale)} ·{" "}
                        {typeLabels[tx.type] ?? tx.type}
                      </p>
                    </div>
                    <div className="text-right">
                      <p
                        className={cn(
                          "text-sm font-medium",
                          isCredit
                            ? "text-green-600 dark:text-green-400"
                            : "text-red-600 dark:text-red-400",
                        )}
                      >
                        {isCredit ? "+" : ""}
                        {formatMoney(tx.amount, tx.currency, i18n.locale, MONEY_2)}
                      </p>
                      {tx.originalCurrency && tx.originalAmount != null && (
                        <p className="text-muted-foreground/70 text-[0.625rem]">
                          {formatMoney(
                            tx.originalAmount,
                            tx.originalCurrency,
                            i18n.locale,
                            MONEY_2,
                          )}
                        </p>
                      )}
                      <p className="text-muted-foreground text-[0.625rem]">
                        {tx.ledger === "subscription_credit"
                          ? "—"
                          : formatMoney(tx.balanceAfter, tx.currency, i18n.locale, MONEY_2)}
                      </p>
                    </div>
                  </div>

                  {/* Desktop */}
                  <div className="hidden items-center md:grid md:grid-cols-5 md:gap-2">
                    <div className="text-muted-foreground text-sm">
                      {new Date(tx.createdAt).toLocaleDateString(i18n.locale)}
                    </div>
                    <div className="col-span-2 flex items-center gap-2">
                      <TxIcon credit={isCredit} small />
                      <div className="min-w-0">
                        <p className="text-foreground truncate text-sm">{tx.description}</p>
                        <p className="text-muted-foreground text-xs">
                          {typeLabels[tx.type] ?? tx.type}
                          {tx.originalCurrency && tx.originalAmount != null && (
                            <span className="text-muted-foreground/70">
                              {" · "}
                              {formatMoney(
                                tx.originalAmount,
                                tx.originalCurrency,
                                i18n.locale,
                                MONEY_2,
                              )}{" "}
                              → {tx.currency}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p
                        className={cn(
                          "text-sm font-medium",
                          isCredit
                            ? "text-green-600 dark:text-green-400"
                            : "text-red-600 dark:text-red-400",
                        )}
                      >
                        {isCredit ? "+" : ""}
                        {formatMoney(tx.amount, tx.currency, i18n.locale, MONEY_2)}
                      </p>
                    </div>
                    <div className="text-muted-foreground text-right text-sm">
                      {/* A subscription-credit row's balance is that credit's, not the wallet's. */}
                      {tx.ledger === "subscription_credit"
                        ? "—"
                        : formatMoney(tx.balanceAfter, tx.currency, i18n.locale, MONEY_2)}
                    </div>
                  </div>
                </m.div>
              );
            })}
          </div>

          <Pagination
            page={page}
            total={pagination.total}
            limit={pagination.limit}
            onPageChange={setPage}
          />
        </>
      ) : (
        <div className="bg-card rounded-xl border px-4 py-12 text-center">
          <WalletIcon className="text-muted-foreground mx-auto mb-3 size-10" />
          <p className="text-muted-foreground text-sm">
            <Trans>No transactions yet.</Trans>
          </p>
        </div>
      )}
    </div>
  );
}

function SummaryStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="text-foreground text-lg font-bold">{value}</p>
    </div>
  );
}

function TxIcon({ credit, small }: { credit: boolean; small?: boolean }) {
  const sizeWrap = small ? "size-6" : "size-8";
  const sizeIcon = small ? "size-3" : "size-3.5";
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full",
        sizeWrap,
        credit
          ? "bg-green-100 text-green-600 dark:bg-green-950 dark:text-green-400"
          : "bg-red-100 text-red-600 dark:bg-red-950 dark:text-red-400",
      )}
    >
      {credit ? <ArrowDownLeft className={sizeIcon} /> : <ArrowUpRight className={sizeIcon} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Payouts panel
// ---------------------------------------------------------------------------

function PayoutsPanel({
  balance,
  onRefresh,
}: {
  balance: WalletBalance | null;
  onRefresh: () => void;
}) {
  const { t, i18n } = useLingui();
  const queryClient = useQueryClient();
  const { country } = useBrowsingCountry();
  const countryCode = (country ?? "ro").toLowerCase();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingPayoutId, setUploadingPayoutId] = useState<string | null>(null);
  const [showPayoutForm, setShowPayoutForm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [fiscalType, setFiscalType] = useState<FiscalTypeCode>("individual");
  const [businessName, setBusinessName] = useState("");
  const [vatNumber, setVatNumber] = useState("");
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [bankIban, setBankIban] = useState("");
  const [bankSwift, setBankSwift] = useState("");
  const [selfBillingAccepted, setSelfBillingAccepted] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(() => new Date().toISOString().split("T")[0]);
  // Changing the payout IBAN and requesting a payout move money: the API
  // answers vault_locked while a PIN guarding the wallet is not unlocked — the
  // gate opens and, once unlocked, the call runs once more. The balance, the
  // history and the details on file stay readable (a payout only ever goes to
  // the IBAN on file, which takes the unlock to change).
  const { triggerProtected, runProtected, PinGateElement } = useProtectedAction("wallet");

  const query = useQuery({
    queryKey: ["api", "/api/account/wallet", "payouts"],
    queryFn: () => apiFetch.get<PayoutsResponse>("/api/account/wallet?tab=payouts"),
  });

  // Hydrate form once data lands.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current || !query.data) return;
    hydratedRef.current = true;
    const info = query.data.payoutInfo;
    setFiscalType(info.fiscalType ?? "individual");
    setBusinessName(info.businessName ?? "");
    setVatNumber(info.vatNumber ?? "");
    setRegistrationNumber(info.registrationNumber ?? "");
    setBankSwift(info.bankSwift ?? "");
    setSelfBillingAccepted(!!info.selfBillingAccepted);
  }, [query.data]);

  // The IBAN on its own: while the wallet is locked the API sends only its
  // last four characters (`bankIbanMasked`), and that must never reach the
  // field — saved back, it would replace the real IBAN. It fills in once the
  // API sends it in full (after an unlock), unless the person typed one.
  const ibanHydratedRef = useRef(false);
  useEffect(() => {
    const info = query.data?.payoutInfo;
    if (!info || info.bankIbanMasked || ibanHydratedRef.current) return;
    ibanHydratedRef.current = true;
    setBankIban((typed) => typed || (info.bankIban ?? ""));
  }, [query.data]);

  /** Unlock the wallet (PIN gate), then read the payout details again — the IBAN in full. */
  const revealIban = () => {
    // The API holds no unlock (it masked the IBAN): any hint this tab keeps is stale.
    clearVaultUnlockHint();
    triggerProtected(() => {
      void queryClient.invalidateQueries({ queryKey: ["api", "/api/account/wallet", "payouts"] });
    });
  };

  const currentFiscalConfig = FISCAL_TYPES.find((f) => f.code === fiscalType) ?? FISCAL_TYPES[0];
  const needsBusinessName = currentFiscalConfig.requiresBusinessName;
  const needsVatNumber = currentFiscalConfig.requiresVatNumber;
  const needsRegistrationNumber = currentFiscalConfig.requiresRegistrationNumber;
  const isSelfBilling = currentFiscalConfig.selfBillingEligible;

  const saveMutation = useMutation({
    mutationFn: () => {
      const toast = showToast(t`Saving…`, "loading");
      return runProtected(() =>
        apiFetch.post("/api/account/wallet/payout-info", {
          fiscalType,
          businessName,
          vatNumber,
          registrationNumber,
          bankIban,
          bankSwift,
          currency: balance?.currency ?? "EUR",
          selfBillingAccepted: fiscalType === "individual" ? selfBillingAccepted : undefined,
        }),
      )
        .then((res) => {
          toast.update(t`Saved`, "success");
          return res;
        })
        .catch((err) => {
          // The PIN gate was dismissed: nothing was sent.
          if (isVaultUnlockCancelled(err)) {
            toast.dismiss();
            throw err;
          }
          const msg = err instanceof ApiError ? extractError(err) : t`Could not save changes.`;
          toast.update(msg, "error");
          throw err;
        });
    },
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ["api", "/api/account/wallet", "payouts"] });
    },
    onError: (err) => {
      if (isVaultUnlockCancelled(err)) return;
      const msg = err instanceof ApiError ? extractError(err) : t`Could not save changes.`;
      setError(msg);
    },
  });

  const submitPayoutMutation = useMutation({
    mutationFn: () => {
      const toast = showToast(t`Submitting request…`, "loading");
      return runProtected(() =>
        apiFetch.post("/api/account/wallet/request-payout", {
          amount: parseFloat(payoutAmount),
          invoiceNumber: invoiceNumber || null,
          invoiceDate: invoiceDate || null,
        }),
      )
        .then((res) => {
          toast.update(t`Request submitted`, "success");
          return res;
        })
        .catch((err) => {
          // The PIN gate was dismissed: nothing was sent.
          if (isVaultUnlockCancelled(err)) {
            toast.dismiss();
            throw err;
          }
          const msg = err instanceof ApiError ? extractError(err) : t`Could not submit request.`;
          toast.update(msg, "error");
          throw err;
        });
    },
    onSuccess: () => {
      setShowPayoutForm(false);
      setPayoutAmount("");
      setInvoiceNumber("");
      setError(null);
      onRefresh();
      queryClient.invalidateQueries({ queryKey: ["api", "/api/account/wallet", "payouts"] });
    },
    onError: (err) => {
      if (isVaultUnlockCancelled(err)) return;
      const msg = err instanceof ApiError ? extractError(err) : t`Could not submit request.`;
      setError(msg);
    },
  });

  const uploadInvoice = async (payoutId: string, file: File) => {
    setUploadingPayoutId(payoutId);
    setError(null);
    const toast = showToast(t`Uploading…`, "loading");
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("payoutId", payoutId);
      // FormData must go through raw() so the browser sets the multipart
      // boundary — but raw() resolves for 4xx/5xx too, so without this check a
      // rejected upload (bad payoutId, oversized/wrong-MIME file, 500) still
      // showed "Uploaded" while the payout stayed blocked on a missing invoice.
      // Re-throwing an ApiError routes it into the same catch as every other
      // mutation here, so extractError() surfaces the server's message.
      const res = await apiFetch.raw("/api/account/wallet/upload-invoice", {
        method: "POST",
        body: formData,
      });
      if (!res.ok) {
        throw new ApiError(res.status, await res.json().catch(() => null));
      }
      toast.update(t`Uploaded`, "success");
      queryClient.invalidateQueries({ queryKey: ["api", "/api/account/wallet", "payouts"] });
    } catch (err) {
      const msg = err instanceof ApiError ? extractError(err) : t`Upload failed.`;
      setError(msg);
      toast.update(msg, "error");
    } finally {
      setUploadingPayoutId(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (query.isLoading) return <PayoutsSkeleton />;
  if (query.isError) return <ErrorState onRetry={() => query.refetch()} />;

  const { payoutInfo, payouts } = query.data!;
  const minPayout = Number(
    (balance?.referralTerms as Record<string, unknown> | null)?.min_payout ?? 50,
  );

  const hasBasicInfo = !!payoutInfo.bankIban;
  // The IBAN on file came masked and nothing was typed: the field stays empty.
  const ibanOnFileMasked = payoutInfo.bankIbanMasked === true && !!payoutInfo.bankIban && !bankIban;
  const ibanLastFour = (payoutInfo.bankIban ?? "").replace(/[^0-9A-Za-z]/g, "").slice(-4);
  const hasBusinessInfo = needsBusinessName ? !!payoutInfo.businessName : true;
  const hasSelfBilling = isSelfBilling ? payoutInfo.selfBillingAccepted : true;
  const isPayoutReady = hasBasicInfo && hasBusinessInfo && hasSelfBilling;
  const canRequestPayout = isPayoutReady && (balance?.available ?? 0) >= minPayout;
  const fmt = (n: number) => formatMoney(n, balance?.currency || "EUR", i18n.locale, MONEY_2);
  const availableLabel = fmt(balance?.available ?? 0);
  const minimumLabel = fmt(minPayout);
  const selfBillingAcceptedOn = payoutInfo.selfBillingAcceptedAt
    ? new Date(payoutInfo.selfBillingAcceptedAt).toLocaleDateString(i18n.locale)
    : "";

  const wht = getWithholdingTax(countryCode, fiscalType);

  // Legacy resolves `fiscalTypeInfo_${fiscalType}_withholding` for ANY fiscal
  // type when withholding is required, otherwise `fiscalTypeInfo_${fiscalType}`
  // (legacy payouts-tab.tsx). Only the individual withholding variant has copy
  // in the legacy catalogs (getWithholdingTax never requires withholding for
  // businesses), so other types fall back to their standard copy.
  const fiscalTypeInfo: Record<FiscalTypeCode, React.ReactNode> = {
    individual: (
      <Trans>
        As an individual, Appy Solutions Ltd (Bulgaria) will issue a self-billing invoice on your
        behalf for each payout. No business registration needed — just provide your full name and
        bank details. You are solely responsible for declaring this income and paying applicable
        taxes in your country of residence. Appy Solutions does not withhold income tax at source.
      </Trans>
    ),
    freelancer: (
      <Trans>
        As a registered self-employed professional, you issue your own invoice to Appy Solutions
        Ltd, Ivan Susanin 45, Sofia, Bulgaria. Include your registration/tax number. If you are
        VAT-registered in an EU country other than Bulgaria, apply reverse charge (Art. 196 VAT
        Directive) — invoice without VAT. All payouts are in EUR.
      </Trans>
    ),
    small_company: (
      <Trans>
        As a company, you issue invoices with VAT (if VAT-registered) to Appy Solutions Ltd,
        Bulgaria. For EU cross-border B2B: apply reverse charge — invoice without VAT, stating
        "Reverse charge — Art. 196 VAT Directive" and both VAT numbers. All payouts are in EUR.
      </Trans>
    ),
    corporation: (
      <Trans>
        As a corporation, you issue invoices with VAT to Appy Solutions Ltd, Bulgaria. Reverse
        charge applies for EU cross-border B2B transactions. Include both VAT numbers and the
        mention "Reverse charge — Art. 196 VAT Directive". All payouts are in EUR.
      </Trans>
    ),
  };
  const fiscalTypeWithholdingInfo: Partial<Record<FiscalTypeCode, React.ReactNode>> = {
    individual: (
      <Trans>
        As an individual in Bulgaria, Appy Solutions Ltd will issue a self-billing invoice on your
        behalf. A 10% advance income tax will be withheld at source on each payout (per ZDDDFL Art.
        43). The net amount (after tax) will be transferred to your bank account. Appy Solutions
        will file the quarterly tax declaration (Art. 55) and provide you with a certificate of
        withheld tax for your annual declaration.
      </Trans>
    ),
  };
  const fiscalTypeInfoText = wht.required
    ? (fiscalTypeWithholdingInfo[fiscalType] ?? fiscalTypeInfo[fiscalType])
    : fiscalTypeInfo[fiscalType];

  const statusConfig: Record<string, { color: string; label: string }> = {
    pending_invoice: {
      color: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400",
      label: t`Awaiting invoice`,
    },
    invoice_received: {
      color: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400",
      label: t`Invoice received`,
    },
    processing: {
      color: "bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-400",
      label: t`Processing`,
    },
    paid: {
      color: "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400",
      label: t`Paid`,
    },
    cancelled: {
      color: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400",
      label: t`Cancelled`,
    },
  };

  return (
    <div className="space-y-5 pb-6">
      {/* Business info form */}
      <m.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-card rounded-xl border p-5"
      >
        <h2 className="text-sm font-semibold">
          <Trans>Business info</Trans>
        </h2>
        <p className="text-muted-foreground mb-4 text-xs">
          <Trans>How we should pay you and which legal form you use.</Trans>
        </p>

        {/* Fiscal type selector */}
        <div className="mb-4 space-y-1.5">
          <label className="text-xs font-medium">
            <Trans>Fiscal type</Trans>
          </label>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {FISCAL_TYPES.map((ft) => {
              const localLabel = getFiscalLocalLabel(countryCode, ft.code);
              const ftLabel: Record<FiscalTypeCode, string> = {
                individual: t`Individual`,
                freelancer: t`Freelancer`,
                small_company: t`Small company`,
                corporation: t`Corporation`,
              };
              return (
                <button
                  key={ft.code}
                  type="button"
                  onClick={() => setFiscalType(ft.code)}
                  className={cn(
                    "min-h-[44px] rounded-lg border px-3 py-2 text-sm transition-colors",
                    fiscalType === ft.code
                      ? "border-primary bg-primary/10 text-primary font-medium"
                      : "border-border bg-card text-muted-foreground hover:bg-muted",
                  )}
                >
                  <span className="block text-xs leading-tight">{ftLabel[ft.code]}</span>
                  <span className="text-muted-foreground block text-[0.625rem] leading-tight">
                    {localLabel}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Fiscal type info banner */}
        <div
          className={cn(
            "mb-4 flex items-start gap-2 rounded-lg p-3",
            wht.required
              ? "border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/50"
              : "bg-muted/50",
          )}
        >
          <Info
            className={cn(
              "mt-0.5 size-4 shrink-0",
              wht.required ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground",
            )}
          />
          <p
            className={cn(
              "text-xs",
              wht.required ? "text-amber-800 dark:text-amber-300" : "text-muted-foreground",
            )}
          >
            {fiscalTypeInfoText}
          </p>
        </div>

        {!isSelfBilling && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-900 dark:bg-blue-950/50">
            <Info className="mt-0.5 size-4 shrink-0 text-blue-600 dark:text-blue-400" />
            <p className="text-xs text-blue-800 dark:text-blue-300">
              <Trans>
                You are responsible for issuing the invoice. We pay against your invoice.
              </Trans>
            </p>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          {needsBusinessName && (
            <FormField label={t`Business name`}>
              <Input
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="—"
              />
            </FormField>
          )}
          {needsRegistrationNumber && (
            <FormField label={t`Registration number`}>
              <Input
                value={registrationNumber}
                onChange={(e) => setRegistrationNumber(e.target.value)}
                placeholder="—"
              />
            </FormField>
          )}
          {needsVatNumber && (
            <FormField
              label={
                fiscalType === "freelancer"
                  ? `${t`VAT number`} (${t`Optional`})`
                  : `${t`VAT number`} (${t`Required`})`
              }
            >
              <Input
                value={vatNumber}
                onChange={(e) => setVatNumber(e.target.value)}
                placeholder="—"
              />
            </FormField>
          )}
          <FormField label={t`IBAN`}>
            <Input
              value={bankIban}
              onChange={(e) => setBankIban(e.target.value)}
              placeholder={
                ibanOnFileMasked ? (payoutInfo.bankIban ?? "") : "RO49 AAAA 1B31 0075 9384 0000"
              }
              className="font-mono uppercase"
            />
          </FormField>
          {ibanOnFileMasked && (
            <div className="bg-muted text-muted-foreground flex flex-col gap-2 rounded-lg p-3 text-xs sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="flex items-start gap-2">
                <Lock className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                <span>
                  <Trans>
                    The IBAN on file ends in {ibanLastFour}. Unlock your wallet to see it in full
                    and keep it, or enter a new one.
                  </Trans>
                </span>
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={revealIban}
                className="min-h-[44px] shrink-0"
              >
                <Trans>Unlock</Trans>
              </Button>
            </div>
          )}
          <FormField label={t`SWIFT/BIC`}>
            <Input
              value={bankSwift}
              onChange={(e) => setBankSwift(e.target.value)}
              placeholder="BTRLRO22"
              className="font-mono uppercase"
            />
          </FormField>
        </div>

        {isSelfBilling && (
          <div className="mt-4">
            <label className="hover:bg-muted/50 flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors">
              <input
                type="checkbox"
                checked={selfBillingAccepted}
                onChange={(e) => setSelfBillingAccepted(e.target.checked)}
                className="accent-primary mt-0.5 size-4 shrink-0"
              />
              <div className="space-y-1">
                <span className="text-xs font-medium">
                  <Trans>I accept the self-billing arrangement</Trans>
                </span>
                <p className="text-muted-foreground text-[0.625rem] leading-relaxed">
                  <Trans>
                    I authorize Appy Solutions Ltd (Bulgaria) to issue a self-billing invoice on my
                    behalf for each payout, in accordance with Art. 224 of the EU VAT Directive. I
                    confirm that my details are accurate and I am solely responsible for declaring
                    this income in my country of residence.
                  </Trans>
                </p>
              </div>
            </label>
            {payoutInfo.selfBillingAccepted && payoutInfo.selfBillingAcceptedAt && (
              <div className="mt-2 flex items-center gap-1.5 text-[0.625rem] text-green-600 dark:text-green-400">
                <CheckCircle2 className="size-3" />
                <Trans>Accepted on {selfBillingAcceptedOn}</Trans>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-950 dark:text-red-400">
            <AlertCircle className="size-4 shrink-0" />
            {error}
          </div>
        )}

        <div className="mt-4 flex items-center gap-3">
          <Button
            size="sm"
            onClick={() => saveMutation.mutate()}
            disabled={
              saveMutation.isPending ||
              !bankIban ||
              (needsBusinessName && !businessName) ||
              (needsRegistrationNumber && !registrationNumber)
            }
            className="min-h-[44px]"
          >
            {saveMutation.isPending ? <Trans>Saving…</Trans> : <Trans>Save</Trans>}
          </Button>
        </div>
      </m.div>

      {/* Request payout */}
      <m.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-card rounded-xl border p-5"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">
            <Trans>Request payout</Trans>
          </h2>
          {!showPayoutForm && (
            <Button
              size="sm"
              onClick={() => setShowPayoutForm(true)}
              disabled={!canRequestPayout}
              className="min-h-[44px]"
            >
              <Trans>Request payout</Trans>
            </Button>
          )}
        </div>

        {!isPayoutReady && (
          <p className="text-muted-foreground mt-2 text-xs">
            {!hasBasicInfo ? (
              <Trans>Complete your business info first.</Trans>
            ) : !hasSelfBilling ? (
              <Trans>Accept the self-billing arrangement above first.</Trans>
            ) : (
              <Trans>Complete your business info first.</Trans>
            )}
          </p>
        )}

        {isPayoutReady && !showPayoutForm && (
          <div className="mt-2 flex flex-wrap gap-x-4 text-xs">
            <span className="text-muted-foreground">
              <Trans>Available: {availableLabel}</Trans>
            </span>
            <span className="text-muted-foreground">
              <Trans>Minimum: {minimumLabel}</Trans>
            </span>
          </div>
        )}

        <AnimatePresence>
          {showPayoutForm && (
            <m.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="mt-4 space-y-4">
                <div className="text-muted-foreground space-y-1 text-xs">
                  <p>
                    <Trans>Available: {availableLabel}</Trans>
                  </p>
                  <p>
                    <Trans>Minimum: {minimumLabel}</Trans>
                  </p>
                </div>

                <FormField label={t`Amount`}>
                  <Input
                    type="number"
                    min={minPayout}
                    max={balance?.available ?? 0}
                    step="0.01"
                    value={payoutAmount}
                    onChange={(e) => setPayoutAmount(e.target.value)}
                    placeholder={(balance?.available ?? 0).toFixed(2)}
                    className="max-w-xs"
                  />
                  <p className="text-muted-foreground text-[0.625rem]">
                    <Trans>Max: {availableLabel}</Trans>
                  </p>
                </FormField>

                {!isSelfBilling && (
                  <>
                    <div className="flex items-start gap-2 rounded-lg bg-blue-50 p-3 dark:bg-blue-950/50">
                      <Info className="mt-0.5 size-4 shrink-0 text-blue-600 dark:text-blue-400" />
                      <p className="text-xs text-blue-800 dark:text-blue-300">
                        <Trans>
                          Issue an invoice to Appy Solutions and provide its number + date.
                        </Trans>
                      </p>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <FormField label={t`Invoice number`}>
                        <Input
                          value={invoiceNumber}
                          onChange={(e) => setInvoiceNumber(e.target.value)}
                          placeholder="INV-001"
                        />
                      </FormField>
                      <FormField label={t`Invoice date`}>
                        <Input
                          type="date"
                          value={invoiceDate}
                          onChange={(e) => setInvoiceDate(e.target.value)}
                        />
                      </FormField>
                    </div>
                  </>
                )}

                {isSelfBilling && (
                  <div className="flex items-start gap-2 rounded-lg bg-green-50 p-3 dark:bg-green-950/50">
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-green-600 dark:text-green-400" />
                    <p className="text-xs text-green-800 dark:text-green-300">
                      <Trans>
                        We'll generate the invoice on your behalf — no action required from you.
                      </Trans>
                    </p>
                  </div>
                )}

                {error && (
                  <div className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-950 dark:text-red-400">
                    <AlertCircle className="size-4 shrink-0" />
                    {error}
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowPayoutForm(false);
                      setError(null);
                    }}
                    className="min-h-[44px]"
                  >
                    <Trans>Cancel</Trans>
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => submitPayoutMutation.mutate()}
                    disabled={
                      submitPayoutMutation.isPending ||
                      !payoutAmount ||
                      parseFloat(payoutAmount) < minPayout ||
                      parseFloat(payoutAmount) > (balance?.available ?? 0)
                    }
                    className="min-h-[44px]"
                  >
                    {submitPayoutMutation.isPending ? (
                      <Trans>Submitting…</Trans>
                    ) : (
                      <Trans>Submit request</Trans>
                    )}
                  </Button>
                </div>
              </div>
            </m.div>
          )}
        </AnimatePresence>
      </m.div>

      {/* Payout history */}
      <m.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-card rounded-xl border"
      >
        <div className="border-b px-4 py-3">
          <h2 className="text-sm font-semibold">
            <Trans>Payout history</Trans>
          </h2>
        </div>

        {payouts.length > 0 ? (
          <>
            <div className="text-muted-foreground hidden border-b px-4 py-2 text-xs font-medium md:grid md:grid-cols-5 md:gap-2">
              <div>
                <Trans>Invoice #</Trans>
              </div>
              <div>
                <Trans>Date</Trans>
              </div>
              <div>
                <Trans>Amount</Trans>
              </div>
              <div>
                <Trans>Status</Trans>
              </div>
              <div className="text-right">
                <Trans>Actions</Trans>
              </div>
            </div>

            <div className="divide-y">
              {payouts.map((p) => {
                const sc = statusConfig[p.status] ?? statusConfig.pending_invoice;
                return (
                  <div key={p.id} className="px-4 py-3">
                    {/* Mobile */}
                    <div className="flex items-center justify-between md:hidden">
                      <div>
                        <p className="text-foreground text-sm font-medium">{fmt(p.amount)}</p>
                        <div className="mt-1 flex items-center gap-2">
                          <span className="text-muted-foreground text-xs">
                            {new Date(p.createdAt).toLocaleDateString(i18n.locale)}
                          </span>
                          <Badge className={`text-[0.625rem] ${sc.color}`}>{sc.label}</Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {p.status === "pending_invoice" && (
                          <button
                            onClick={() => {
                              setUploadingPayoutId(p.id);
                              fileInputRef.current?.click();
                            }}
                            disabled={uploadingPayoutId === p.id}
                            className="text-primary flex min-h-[44px] min-w-[44px] items-center justify-center"
                            aria-label={t`Upload invoice`}
                          >
                            {uploadingPayoutId === p.id ? (
                              <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            ) : (
                              <Upload className="size-4" />
                            )}
                          </button>
                        )}
                        {p.invoiceUrl && (
                          <a
                            href={p.invoiceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary flex min-h-[44px] min-w-[44px] items-center justify-center"
                            aria-label={t`Download invoice`}
                          >
                            <Download className="size-4" />
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Desktop */}
                    <div className="hidden items-center md:grid md:grid-cols-5 md:gap-2">
                      <div className="text-foreground text-sm">{p.invoiceNumber ?? "—"}</div>
                      <div className="text-muted-foreground text-sm">
                        {new Date(p.createdAt).toLocaleDateString(i18n.locale)}
                      </div>
                      <div className="text-foreground text-sm font-medium">{fmt(p.amount)}</div>
                      <div>
                        <Badge className={`text-[0.625rem] ${sc.color}`}>{sc.label}</Badge>
                      </div>
                      <div className="flex items-center justify-end gap-2">
                        {p.status === "pending_invoice" && (
                          <button
                            onClick={() => {
                              setUploadingPayoutId(p.id);
                              fileInputRef.current?.click();
                            }}
                            disabled={uploadingPayoutId === p.id}
                            className="text-primary inline-flex min-h-[44px] items-center gap-1 text-sm hover:underline"
                          >
                            {uploadingPayoutId === p.id ? (
                              <span className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            ) : (
                              <Upload className="size-3.5" />
                            )}
                            <Trans>Upload</Trans>
                          </button>
                        )}
                        {p.invoiceUrl && (
                          <a
                            href={p.invoiceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary inline-flex min-h-[44px] items-center gap-1 text-sm hover:underline"
                          >
                            <Download className="size-3.5" />
                            <Trans>Download</Trans>
                          </a>
                        )}
                        {!p.invoiceUrl && p.status !== "pending_invoice" && (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="px-4 py-8 text-center">
            <Banknote className="text-muted-foreground mx-auto mb-3 size-10" />
            <p className="text-muted-foreground text-sm">
              <Trans>No payouts yet.</Trans>
            </p>
          </div>
        )}
      </m.div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && uploadingPayoutId) uploadInvoice(uploadingPayoutId, file);
        }}
      />
      {PinGateElement}
    </div>
  );
}

function PayoutsSkeleton() {
  return (
    <div className="space-y-5 pb-6">
      <Skeleton className="h-64 w-full rounded-xl" />
      <Skeleton className="h-32 w-full rounded-xl" />
      <Skeleton className="h-40 w-full rounded-xl" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared building blocks
// ---------------------------------------------------------------------------

function FormField({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={cn("flex flex-col gap-1.5", className)}>
      <span className="text-foreground text-xs font-medium">{label}</span>
      {children}
    </label>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground font-medium">{value}</span>
    </div>
  );
}

function Pagination({
  page,
  total,
  limit,
  onPageChange,
}: {
  page: number;
  total: number;
  limit: number;
  onPageChange: (next: number) => void;
}) {
  const { t } = useLingui();
  if (total <= limit) return null;
  const totalPages = Math.ceil(total / limit);
  return (
    <div className="flex justify-center gap-2 pt-2">
      <Button
        size="icon-touch"
        variant="outline"
        onClick={() => onPageChange(Math.max(1, page - 1))}
        disabled={page === 1}
        aria-label={t`Previous page`}
      >
        <ChevronLeft className="size-4" aria-hidden />
      </Button>
      <span className="text-muted-foreground flex items-center text-sm">
        <Trans>
          Page {page} of {totalPages}
        </Trans>
      </span>
      <Button
        size="icon-touch"
        variant="outline"
        onClick={() => onPageChange(page + 1)}
        disabled={page * limit >= total}
        aria-label={t`Next page`}
      >
        <ChevronRight className="size-4" aria-hidden />
      </Button>
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="space-y-3 pb-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-16 rounded-xl" />
      ))}
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <LoadErrorState
      className="mt-2"
      title={<Trans>Could not load wallet.</Trans>}
      onRetry={onRetry}
    />
  );
}

function extractError(err: ApiError): string {
  if (typeof err.body === "object" && err.body !== null && "error" in err.body) {
    const msg = (err.body as { error?: unknown }).error;
    if (typeof msg === "string") return msg;
  }
  return `Error ${err.status}`;
}
